const CANONICAL_SELECTOR = 'link[rel="canonical"]';
const OG_URL_SELECTOR = 'meta[property="og:url"]';
const OG_TITLE_SELECTOR = 'meta[property="og:title"]';
const TWITTER_TITLE_SELECTOR = 'meta[name="twitter:title"]';
const DESCRIPTION_SELECTOR = 'meta[name="description"]';
const OG_DESCRIPTION_SELECTOR = 'meta[property="og:description"]';

function getSafeOrigin(win) {
  return win?.location?.origin || "https://speakyourmenu.com";
}

export function normalizeComparablePathname(pathname) {
  const value = String(pathname || "").trim();
  if (!value) return "/";

  try {
    const parsed = new URL(value, "https://speakyourmenu.com");
    const normalized = String(parsed.pathname || "/").replace(/\/+$/g, "");
    return normalized || "/";
  } catch {
    const withLeadingSlash = value.startsWith("/") ? value : `/${value}`;
    const normalized = withLeadingSlash.replace(/\/+$/g, "");
    return normalized || "/";
  }
}

function getCanonicalHref(doc) {
  if (!doc) return "";
  return doc.querySelector(CANONICAL_SELECTOR)?.getAttribute("href") || "";
}

function parseCanonicalPathname(canonicalHref, win) {
  if (!canonicalHref) return "";
  try {
    const parsed = new URL(canonicalHref, getSafeOrigin(win));
    return normalizeComparablePathname(parsed.pathname);
  } catch {
    return "";
  }
}

function getMetaContent(doc, selector) {
  if (!doc) return "";
  const value = doc.querySelector(selector)?.getAttribute("content") || "";
  return String(value).trim();
}

function parsePathnameFromUrl(urlLike, win) {
  if (!urlLike) return "";
  try {
    const parsed = new URL(urlLike, getSafeOrigin(win));
    return normalizeComparablePathname(parsed.pathname);
  } catch {
    return "";
  }
}

function buildRouteSignals(snapshot) {
  if (!snapshot) {
    return {
      ogUrlPathname: "",
      description: "",
      ogDescription: "",
      ogTitle: "",
      twitterTitle: "",
    };
  }

  return {
    ogUrlPathname: String(snapshot.ogUrlPathname || ""),
    description: String(snapshot.description || ""),
    ogDescription: String(snapshot.ogDescription || ""),
    ogTitle: String(snapshot.ogTitle || ""),
    twitterTitle: String(snapshot.twitterTitle || ""),
  };
}

function haveRouteSignalsChanged(startSnapshot, snapshot) {
  const start = buildRouteSignals(startSnapshot);
  const current = buildRouteSignals(snapshot);

  return (
    start.ogUrlPathname !== current.ogUrlPathname ||
    start.description !== current.description ||
    start.ogDescription !== current.ogDescription ||
    start.ogTitle !== current.ogTitle ||
    start.twitterTitle !== current.twitterTitle
  );
}

function isTitleConsistentWithSnapshot(snapshot) {
  const title = String(snapshot?.title || "").trim();
  if (!title) return false;

  const ogTitle = String(snapshot?.ogTitle || "").trim();
  if (ogTitle && title !== ogTitle) return false;

  const twitterTitle = String(snapshot?.twitterTitle || "").trim();
  if (twitterTitle && title !== twitterTitle) return false;

  return true;
}

export function getPageMetadataSnapshot(doc = typeof document !== "undefined" ? document : null, win = typeof window !== "undefined" ? window : null) {
  const title = String(doc?.title || "").trim();
  const canonicalHref = getCanonicalHref(doc);
  const canonicalPathname = parseCanonicalPathname(canonicalHref, win);
  const ogUrlPathname = parsePathnameFromUrl(getMetaContent(doc, OG_URL_SELECTOR), win);
  const description = getMetaContent(doc, DESCRIPTION_SELECTOR);
  const ogDescription = getMetaContent(doc, OG_DESCRIPTION_SELECTOR);
  const ogTitle = getMetaContent(doc, OG_TITLE_SELECTOR);
  const twitterTitle = getMetaContent(doc, TWITTER_TITLE_SELECTOR);

  return {
    title,
    hasCanonical: Boolean(canonicalHref),
    canonicalPathname,
    ogUrlPathname,
    description,
    ogDescription,
    ogTitle,
    twitterTitle,
  };
}

export function isPageMetadataReadyForNavigation({
  pathname,
  startSnapshot,
  referenceSnapshot,
  hasObservedMetadataMutation,
  isInitialNavigation,
  snapshot,
}) {
  const currentTitle = String(snapshot?.title || "").trim();
  if (!currentTitle) return false;

  const targetPathname = normalizeComparablePathname(pathname || "/");
  const baselineSnapshot = referenceSnapshot || startSnapshot || null;
  const baselineTitleText = String(baselineSnapshot?.title || "").trim();
  const baselineCanonicalPathname = String(baselineSnapshot?.canonicalPathname || "");
  const titleChanged = currentTitle !== baselineTitleText;
  const routeSignalsChanged = haveRouteSignalsChanged(baselineSnapshot, snapshot);
  const titleConsistentWithMetadata = isTitleConsistentWithSnapshot(snapshot);

  if (snapshot?.hasCanonical) {
    if (!snapshot.canonicalPathname) return false;
    const canonicalMatchesTarget = normalizeComparablePathname(snapshot.canonicalPathname) === targetPathname;
    if (!canonicalMatchesTarget) return false;
    if (!titleConsistentWithMetadata) return false;

    if (isInitialNavigation) return true;

    // Canonical updates can arrive before title updates; never treat canonical alone as complete.
    if (titleChanged) return true;

    // Support legitimate same-title route transitions by requiring additional route-consistent metadata changes.
    return hasObservedMetadataMutation && routeSignalsChanged;
  }

  if (isInitialNavigation) {
    return true;
  }

  const startHadCanonical = Boolean(baselineCanonicalPathname);
  if (startHadCanonical) {
    if (!hasObservedMetadataMutation) return false;
    return titleChanged;
  }

  if (!titleConsistentWithMetadata) return false;

  return titleChanged || (!baselineTitleText && hasObservedMetadataMutation);
}
